import React, { useState, useEffect } from 'react';
import { useWorkspace } from '../context/WorkspaceContext';
import scheduleService from '../services/scheduleService';
import api from '../services/api';
import { X, Plus, GripVertical, Eye, EyeOff, Trash2, Search } from 'lucide-react';
import './ScheduleModal.css';

/**
 * ScheduleModal
 * 
 * Modal for creating and editing schedules with goal collections
 */
function ScheduleModal({ onClose, onComplete, initialData = null, preselectedGoals = [] }) {
    const { currentWorkspace } = useWorkspace();

    // Basic. info
    const [name, setName] = useState(initialData?.name || '');
    const [description, setDescription] = useState(initialData?.description || '');
    const [searchTerm, setSearchTerm] = useState('');

    // Selected Execution Plan
    const [selectedPlanId, setSelectedPlanId] = useState(initialData?.executionPlanId || '');
    const [executionPlans, setExecutionPlans] = useState([]);
    const [showPlanPicker, setShowPlanPicker] = useState(false);

    // Schedule configuration
    const [scheduleType, setScheduleType] = useState(initialData?.scheduleType || 'recurring');
    const [frequency, setFrequency] = useState(() => {
        if (!initialData?.cronExpression) return 'daily';
        const parts = initialData.cronExpression.split(' ');
        if (parts.length < 5) return 'custom';
        const [min, hour, dayOfMonth, month, dayOfWeek] = parts;
        if (dayOfMonth === '*' && month === '*' && dayOfWeek === '*') return 'daily';
        if (dayOfMonth === '*' && month === '*' && dayOfWeek !== '*') return 'weekly';
        return 'custom';
    });
    const [time, setTime] = useState(() => {
        if (!initialData?.cronExpression) return '02:00';
        const parts = initialData.cronExpression.split(' ');
        if (parts.length < 2) return '02:00';
        const [min, hour] = parts;
        if (isNaN(parseInt(min)) || isNaN(parseInt(hour))) return '02:00';
        return `${hour.padStart(2, '0')}:${min.padStart(2, '0')}`;
    });
    const [dayOfWeek, setDayOfWeek] = useState(() => {
        if (!initialData?.cronExpression) return 1;
        const parts = initialData.cronExpression.split(' ');
        if (parts.length < 5) return 1;
        const val = parseInt(parts[4]);
        return isNaN(val) ? 1 : val;
    });
    const [customCron, setCustomCron] = useState(initialData?.cronExpression || '');
    const [timezone, setTimezone] = useState(initialData?.timezone || 'UTC');


    // Data loading
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);

    // Load data on mount
    useEffect(() => {
        loadData();
    }, [currentWorkspace]);

    // Search effect with debounce for execution plans
    useEffect(() => {
        if (!showPlanPicker) return;

        const timer = setTimeout(() => {
            loadExecutionPlans(searchTerm);
        }, 300); // 300ms debounce

        return () => clearTimeout(timer);
    }, [searchTerm, showPlanPicker]);

    const loadData = async () => {
        try {
            setLoading(true);

            // Load execution plans
            await loadExecutionPlans();

        } catch (err) {
            console.error('Failed to load data:', err);
            setError('Failed to load data. Please try again.');
        } finally {
            setLoading(false);
        }
    };

    const loadExecutionPlans = async (search = '') => {
        try {
            const params = { limit: 50 };
            if (search) {
                params.search = search;
            }

            const response = await api.get('/execution-plans', { params });
            setExecutionPlans(response.data.executionPlans || []);
        } catch (err) {
            console.error('Failed to load execution plans:', err);
        }
    };

    const handlePlanSelect = (plan) => {
        setSelectedPlanId(plan.id);
        setShowPlanPicker(false);
    };

    const handleSubmit = async () => {
        // Validation
        if (!name.trim()) {
            setError('Please enter a schedule name');
            return;
        }

        if (!selectedPlanId) {
            setError('Please select an execution plan');
            return;
        }

        setLoading(true);
        setError(null);

        try {
            const scheduleData = {
                name: name.trim(),
                description: description.trim(),
                scheduleType,
                frequency,
                time,
                dayOfWeek: frequency === 'weekly' ? dayOfWeek : undefined,
                cronExpression: frequency === 'custom' ? customCron : undefined,
                timezone,
                executionPlanId: selectedPlanId
            };

            // Basic client-side validation for custom cron frequency (if possible)
            // The backend already validates this strictly.

            if (initialData) {
                await scheduleService.updateSchedule(
                    initialData.id,
                    currentWorkspace.id,
                    scheduleData
                );
            } else {
                await scheduleService.createSchedule(
                    currentWorkspace.id,
                    scheduleData
                );
            }

            if (onComplete) {
                onComplete();
            } else {
                onClose();
            }
        } catch (err) {
            console.error('Failed to save schedule:', err);
            setError(err.response?.data?.error || 'Failed to save schedule. Please try again.');
        } finally {
            setLoading(false);
        }
    };

    return (
        <div className="schedule-modal">
            <div className="schedule-modal__overlay" onClick={onClose} />

            <div className="schedule-modal__container">
                {/* Header */}
                <div className="schedule-modal__header">
                    <h2>{initialData ? 'Edit Schedule' : 'Create Schedule'}</h2>
                    <button className="schedule-modal__close" onClick={onClose}>×</button>
                </div>

                {/* Content */}
                <div className="schedule-modal__content">
                    {/* Basic Info */}
                    <div className="schedule-modal__section">
                        <h3>Basic Information</h3>

                        <div className="schedule-modal__form-group">
                            <label>Schedule Name *</label>
                            <input
                                type="text"
                                value={name}
                                onChange={(e) => setName(e.target.value)}
                                placeholder="Nightly Regression Tests"
                            />
                        </div>

                        <div className="schedule-modal__form-group">
                            <label>Description</label>
                            <textarea
                                value={description}
                                onChange={(e) => setDescription(e.target.value)}
                                placeholder="Optional description..."
                                rows={2}
                            />
                        </div>
                    </div>

                    {/* Execution Plan Selection */}
                    <div className="schedule-modal__section">
                        <h3>Execution Plan</h3>

                        <div className="schedule-modal__form-group">
                            <label>Linked Plan *</label>
                            <button
                                type="button"
                                className="schedule-modal__selector-btn"
                                onClick={() => {
                                    setSearchTerm('');
                                    setShowPlanPicker(true);
                                }}
                            >
                                {selectedPlanId ? (
                                    <span>
                                        {executionPlans.find(p => p.id === selectedPlanId)?.name || 'Selected Plan'}
                                    </span>
                                ) : (
                                    <span className="placeholder">Select execution plan...</span>
                                )}
                            </button>
                        </div>
                    </div>

                    {/* Schedule Configuration */}
                    <div className="schedule-modal__section">
                        <h3>When to Execute</h3>

                        <div className="schedule-modal__form-group">
                            <label>Frequency</label>
                            <select value={frequency} onChange={(e) => setFrequency(e.target.value)}>
                                <option value="daily">Daily</option>
                                <option value="weekly">Weekly</option>
                                <option value="custom">Custom (Cron)</option>
                            </select>
                        </div>

                        {frequency === 'weekly' && (
                            <div className="schedule-modal__form-group">
                                <label>Day of Week</label>
                                <select value={dayOfWeek} onChange={(e) => setDayOfWeek(parseInt(e.target.value))}>
                                    <option value={0}>Sunday</option>
                                    <option value={1}>Monday</option>
                                    <option value={2}>Tuesday</option>
                                    <option value={3}>Wednesday</option>
                                    <option value={4}>Thursday</option>
                                    <option value={5}>Friday</option>
                                    <option value={6}>Saturday</option>
                                </select>
                            </div>
                        )}

                        {frequency === 'custom' ? (
                            <div className="schedule-modal__form-group">
                                <label>Cron Expression</label>
                                <input
                                    type="text"
                                    value={customCron}
                                    onChange={(e) => setCustomCron(e.target.value)}
                                    placeholder="0 2 * * *"
                                />
                                <small>Format: minute hour day month weekday</small>
                            </div>
                        ) : (
                            <div className="schedule-modal__form-group">
                                <label>Time</label>
                                <input
                                    type="time"
                                    value={time}
                                    onChange={(e) => setTime(e.target.value)}
                                />
                            </div>
                        )}

                        <div className="schedule-modal__form-group">
                            <label>Timezone</label>
                            <select value={timezone} onChange={(e) => setTimezone(e.target.value)}>
                                <option value="UTC">UTC (UTC+0)</option>
                                <option value="Africa/Lagos">Lagos (UTC+1)</option>
                                <option value="Africa/Cairo">Cairo (UTC+2)</option>
                                <option value="Africa/Johannesburg">Johannesburg (UTC+2)</option>
                                <option value="Africa/Nairobi">Nairobi (UTC+3)</option>
                                <option value="America/New_York">New York (UTC-5)</option>
                                <option value="America/Chicago">Chicago (UTC-6)</option>
                                <option value="America/Denver">Denver (UTC-7)</option>
                                <option value="America/Los_Angeles">Los Angeles (UTC-8)</option>
                                <option value="America/Sao_Paulo">Sao Paulo (UTC-3)</option>
                                <option value="Asia/Dubai">Dubai (UTC+4)</option>
                                <option value="Asia/Karachi">Karachi (UTC+5)</option>
                                <option value="Asia/Kolkata">Kolkata (UTC+5:30)</option>
                                <option value="Asia/Bangkok">Bangkok (UTC+7)</option>
                                <option value="Asia/Shanghai">Shanghai (UTC+8)</option>
                                <option value="Asia/Tokyo">Tokyo (UTC+9)</option>
                                <option value="Asia/Seoul">Seoul (UTC+9)</option>
                                <option value="Europe/London">London (UTC+0)</option>
                                <option value="Europe/Paris">Paris (UTC+1)</option>
                                <option value="Europe/Berlin">Berlin (UTC+1)</option>
                                <option value="Europe/Moscow">Moscow (UTC+3)</option>
                                <option value="Australia/Sydney">Sydney (UTC+10)</option>
                                <option value="Pacific/Auckland">Auckland (UTC+12)</option>
                            </select>
                        </div>
                    </div>


                    {error && (
                        <div className="schedule-modal__error">
                            {error}
                        </div>
                    )}
                </div>

                {/* Footer */}
                <div className="schedule-modal__footer">
                    <button
                        className="schedule-modal__cancel"
                        onClick={onClose}
                        disabled={loading}
                    >
                        Cancel
                    </button>
                    <button
                        className="schedule-modal__submit"
                        onClick={handleSubmit}
                        disabled={loading || !name.trim() || !selectedPlanId}
                    >
                        {loading ? 'Saving...' : (initialData ? 'Update Schedule' : 'Create Schedule')}
                    </button>
                </div>
            </div>

            {/* Plan Picker Modal */}
            {showPlanPicker && (
                <div className="schedule-modal__picker-overlay" onClick={() => setShowPlanPicker(false)}>
                    <div className="schedule-modal__picker-content" onClick={e => e.stopPropagation()}>
                        <div className="schedule-modal__picker-header">
                            <h3>Select Execution Plan</h3>
                            <button onClick={() => setShowPlanPicker(false)}>×</button>
                        </div>

                        <div className="schedule-modal__picker-search">
                            <Search size={16} className="search-icon" />
                            <input
                                type="text"
                                placeholder="Search plans..."
                                value={searchTerm}
                                onChange={(e) => setSearchTerm(e.target.value)}
                                autoFocus
                            />
                        </div>

                        <div className="schedule-modal__picker-list">
                            {executionPlans.length === 0 ? (
                                <div className="schedule-modal__empty">
                                    {searchTerm ? 'No matching plans found' : 'No execution plans available'}
                                </div>
                            ) : (
                                executionPlans.map(plan => (
                                    <div
                                        key={plan.id}
                                        className={`schedule-modal__picker-item ${selectedPlanId === plan.id ? 'selected' : ''}`}
                                        onClick={() => handlePlanSelect(plan)}
                                    >
                                        <div className="schedule-modal__picker-item-name">
                                            {plan.name}
                                        </div>
                                        <div className="schedule-modal__picker-item-meta">
                                            <span className="platform-badge">{plan.platform}</span>
                                            {plan.totalGoals && <span>{plan.totalGoals} goals</span>}
                                        </div>
                                    </div>
                                ))
                            )}
                        </div>
                    </div>
                </div>
            )}

        </div>
    );
}

export default ScheduleModal;
